<template>
  <v-dialog v-model="visible" max-width="500" :persistent="saving">
    <v-card>
      <v-card-title>{{ $t('markup.classification') }}</v-card-title>
      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary" />
        <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>
        <template v-if="loaded">
          <v-select
            v-if="securityGroup.node_cardinality === 'single'"
            v-model="selectedMark"
            :items="marks"
            item-value="security_mark_id"
            item-text="security_mark_key"
            :label="securityGroup.security_group_key"
            :disabled="saving || !canWrite"
            outlined
            clearable
          />
          <template v-else>
            <div>{{ securityGroup.security_group_key }}</div>
            <v-checkbox
              v-for="mark in marks"
              :key="mark.security_mark_id"
              v-model="selectedMarks"
              :value="mark.security_mark_id"
              :label="mark.security_mark_key"
              :disabled="saving || !canWrite"
              hide-details
            />
          </template>
          <p v-if="!marks.length" class="mt-3">{{ $t('markup.no_security_marks') }}</p>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" @click="visible = false">{{ $t('markup.cancel') }}</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!loaded || !canWrite || saving" @click="write">
          {{ $t('markup.write') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'VersionClassificationDialog',

  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      loaded: false,
      error: '',
      securityGroup: null,
      securityGroupId: null,
      nodeId: null,
      canWrite: false,
      marks: [],
      selectedMark: null,
      selectedMarks: [],
      loadRequestId: 0
    }
  },

  beforeDestroy() {
    this.loadRequestId++
  },

  methods: {
    async open(securityGroupId, nodeId, canWrite) {
      if (this.saving) return
      const requestId = ++this.loadRequestId
      this.visible = true
      this.loaded = false
      this.loading = false
      this.error = ''
      this.securityGroup = null
      this.marks = []
      this.selectedMark = null
      this.selectedMarks = []
      this.securityGroupId = securityGroupId
      this.nodeId = nodeId
      this.canWrite = canWrite

      if (!securityGroupId || !nodeId) {
        this.error = this.$t('markup.classification_not_configured')
        return
      }

      this.loading = true
      try {
        const [groupResponse, marksResponse, classificationResponse] = await Promise.all([
          api.node.security.getSecurityGroup(securityGroupId),
          api.node.security.getSecurityMarksBySecurityGroup(securityGroupId),
          api.node.classification.getClassificationByNode(nodeId)
        ])
        if (requestId !== this.loadRequestId) return

        const group = groupResponse.data
        if (!['single', 'multiple'].includes(group.node_cardinality)) {
          throw new Error('Unsupported security group cardinality')
        }
        this.securityGroup = group
        this.marks = marksResponse.data.items
        const classifiedIds = new Set(classificationResponse.data.map(item => String(item.security_mark_id)))
        this.selectedMarks = this.marks
          .filter(mark => classifiedIds.has(String(mark.security_mark_id)))
          .map(mark => mark.security_mark_id)
        this.selectedMark = this.selectedMarks[0] ?? null
        this.loaded = true
      } catch (error) {
        if (requestId === this.loadRequestId) this.error = this.$t('markup.classification_load_error')
      } finally {
        if (requestId === this.loadRequestId) this.loading = false
      }
    },

    async write() {
      if (!this.loaded || !this.canWrite || this.saving) return
      this.saving = true
      this.error = ''
      const marks = this.securityGroup.node_cardinality === 'single'
        ? (this.selectedMark == null ? [] : [this.selectedMark])
        : [...this.selectedMarks]
      try {
        await api.node.classification.saveSecurityClassification([{
          node_id: this.nodeId,
          security_group_id: this.securityGroupId,
          security_marks: marks
        }])
        this.visible = false
        this.$emit('saved', { nodeId: this.nodeId, securityGroupId: this.securityGroupId, marks })
      } catch (error) {
        this.error = this.$t('markup.classification_write_error')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
